import { onScopeDispose, readonly, ref, watch, type Ref } from 'vue';

/** LaunchDarkly methods used to identify and reconcile the current user. */
export interface LaunchDarklyIdentifier {
  identify(context: { kind: 'user'; key: string }): Promise<unknown>;
  getContext(): { kind?: string; key?: string } | null;
}

// Context: the SDK's identify request sets no timeout of its own, so a stalled one would never
// settle and would hold the app on its provisional permissions for the whole session.
export const IDENTIFY_TIMEOUT_MS = 5000;

/**
 * Keeps LaunchDarkly's context on the signed-in user, so per-user targets and rollouts evaluate
 * for that user and not for the shared key the plugin boots with. `isIdentified` is true once the
 * identify for the latest user has settled: on success the flags are that user's, but after a
 * rejected identify, or one still pending at `IDENTIFY_TIMEOUT_MS`, they are the previous context's.
 * It is also true at once when there is no user, or when flags come from the local file.
 */
export function useLaunchDarklyIdentity(
  client: LaunchDarklyIdentifier,
  isReady: Readonly<Ref<boolean>>,
  userId: () => string | undefined,
  usesLocalFlags: boolean,
): { isIdentified: Readonly<Ref<boolean>> } {
  const isIdentified = ref(false);
  let latestCall = 0;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let isDisposed = false;
  const pendingUsers = new Set<string>();

  const clearIdentifyTimeout = () => {
    clearTimeout(timeout);
    timeout = undefined;
  };

  const identifyUser = async (id: string): Promise<void> => {
    pendingUsers.add(id);
    try {
      await client.identify({ kind: 'user', key: id });
    } finally {
      pendingUsers.delete(id);
    }

    const currentId = userId();
    // The SDK changes its context before resolving: ignoring an old promise cannot undo that.
    // A pending identify for the current user will restore it; otherwise request it again.
    if (
      !isDisposed &&
      isReady.value &&
      currentId &&
      client.getContext()?.key !== currentId &&
      !pendingUsers.has(currentId)
    ) {
      await identifyUser(currentId);
    }
  };

  // Context: the auth store restores the user from localStorage before App mounts, so watching
  // the id alone never fired for a returning session, and every flag kept evaluating the shared
  // boot key. Watching readiness too, from the start, identifies whichever arrives last.
  watch(
    [isReady, userId],
    async ([ready, id]) => {
      // Counted before the early exit, so signing out also discards an identify still in flight.
      const call = ++latestCall;
      clearIdentifyTimeout();

      if (usesLocalFlags || !id) {
        isIdentified.value = true;
        return;
      }

      isIdentified.value = false;
      if (!ready) return;

      const timedOut = new Promise<void>((resolve) => {
        timeout = setTimeout(resolve, IDENTIFY_TIMEOUT_MS);
      });

      try {
        await Promise.race([identifyUser(id), timedOut]);
      } catch {
        // The SDK reports its own failure. Staying unidentified would hold the app on its
        // provisional permissions for the whole session, so it carries on with the previous flags.
      }

      // A slower identify for an earlier user must not mark the current one as identified.
      if (isDisposed || call !== latestCall) return;

      clearIdentifyTimeout();
      isIdentified.value = true;
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    isDisposed = true;
    clearIdentifyTimeout();
  });

  return { isIdentified: readonly(isIdentified) };
}
